import React from 'react';

import { SermonSearchItem, SearchService } from '../../../../src/services/SearchService';
import { TelemetryService } from '../../../../src/services/TelemetryService';
import {ListSubheader,IconButton, Divider, List, CircularProgress, Typography, MenuItem, ListItemText, TextField, InputAdornment, makeStyles} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ErrorIcon from '@material-ui/icons/Error';
import SearchIcon from '@material-ui/icons/Search';
import { EventTracking, SearchEntity } from '../../../../src/utilities/Tracking/EventTracking';
import classNames from 'classnames';
import RatingHelper from '../../../utilities/RatingHelper';
import StringHelper from '../../../utilities/StringHelper';
import InfiniteScroll from 'react-infinite-scroller';
import axios, { CancelTokenSource, CancelTokenStatic } from 'axios';

const useStyles = makeStyles((theme) =>
    ({
        root: {
        },
        toolbar: theme.mixins.toolbar,
        title: {
            paddingTop: 10,
            paddingBottom: 7,
            textAlign: 'left',
            fontSize: 22
        },
        subtitle: {
            paddingTop: theme.spacing(),
            textAlign: 'left',
            fontSize: 19
        },
        closeIcon: {
            float: 'right'
        },
        searchBar: {
            minHeight: '48px',
            maxHeight: '48px',
            width: "95%",
            backgroundColor: "#fff",
            borderRadius: "0",
            margin: '0 auto',
            marginTop: 10
        },
        emptyState: {
            background: '#efefef',
            height: '100%'
        },
        searchWrapper: {
            display: 'flex',
            background: '#efefef',
            minHeight: 70
        },
        inputSearchText: {
            padding: '15px'
        },
        placeHolderIcon: {
            marginTop: "30px",
            width: "50px",
            height: "50px",
            color: "#CCC"
        },
        placeHolderText: {
            fontFamily: "Roboto, Helvetica",
            fontSize: "16px",
            marginTop: "12px",
            color: "#5f6368",
            textAlign: 'center'
        },
        loading: {
            marginTop: theme.spacing(4),
            marginBottom: theme.spacing(1),
            marginLeft: 'auto',
            marginRight: 'auto',
            display: 'block'
        },
        searchResults: {
            overflowY: 'auto'
        },
        bibleCopyright: {
            padding: 5,
            paddingLeft: 16,
            paddingRight: 16,
            '& a': {
                color: theme.palette.primary.main
            }
        },
        copyIcon: {
            display: 'none',
            right: 0,
            top: '30%',
            '&:hover': {
                display: 'block'
            }
        },
        result: {
            whiteSpace: 'normal',
            height: 'auto',
            '&:hover': {
                '&+div': {
                    display: 'block'
                }
            }
        },
        copyInProgress: {
            margin: theme.spacing()
        },
        starIcon: {
            width: 13,
            height: 13
        },
        verticalText: {
            transform: "rotate(-90deg)",
            width: "90px",
            transformOrigin: "45% 85%",
            textAlign: "center",
            lineHeight: "52px"
        },
        grow: {
            flex: 1
        },
    }));

interface Props {
    onDrawerToggle: (open: boolean) => void;
    onSermonSelected: (id: string) => void;
    onCopySermon: (sermonContent: string) => void;
    presetSearchText?: string;
}

type PropsWithClasses = Props & { classes: ReturnType<typeof useStyles> };

interface State {
    queryText: string;
    sermonSearchResult?: SermonSearchItem[];
    totalSermonSearchResultCount: number;
    sermonSearchServerError?: string;
    onCopyInProgress: boolean;
    open: boolean;
}

const currentLocation = "Sermon Drawer Interaction"

class SermonSearchClassComponent extends React.Component<PropsWithClasses, State> {
    cancellationTokenFactory: CancelTokenStatic;
    cancellationToken: CancelTokenSource;

    constructor(props: PropsWithClasses) {
        super(props);

        this.cancellationTokenFactory = axios.CancelToken;
        this.cancellationToken = this.cancellationTokenFactory.source();

        this.state = {
            queryText: props.presetSearchText ? props.presetSearchText : '',
            totalSermonSearchResultCount: 0,
            onCopyInProgress: false,
            open: false
        };
    }

    componentDidMount() {
        if (this.state.queryText.length > 0) {
            this.startSearch();
        }
    }

    startSearch() {
        this.setState({
            totalSermonSearchResultCount: 0,
            sermonSearchResult: undefined,
            sermonSearchServerError: undefined,
        }, this.searchSermons);
        // We want to capture that the user searched something without spamming our server each keypress
        if (this.state.queryText.length === 1) {
            let now = new Date();
            TelemetryService.updateContactField("sc_cloud_explorer", now.toString());
        }
    }

    async searchSermons() {
        const skip = this.state.sermonSearchResult ? this.state.sermonSearchResult.length : 0;
        const take = 10;

        if (skip === 0) {
            this.cancellationToken.cancel('Canceled By User');
            this.cancellationToken = this.cancellationTokenFactory.source()
        }

        const result = await SearchService.findSermons(this.state.queryText, 'english', skip, take, this.cancellationToken);

        let sermonSearchResult = this.state.sermonSearchResult ? this.state.sermonSearchResult : [];

        for (let i = 0; i < result.results.length; i++) {
            sermonSearchResult.push(result.results[i]);
        }

        this.setState({ sermonSearchResult, totalSermonSearchResultCount: result.totalCount }, () => EventTracking.Builder.onSearch(SearchEntity.Sermon, currentLocation));
    }

    async onCopySermon(selectedSermon: SermonSearchItem) {
        this.setState({ onCopyInProgress: true });
        let sermon = await SearchService.getSermon(selectedSermon.id);
        let bodyNoHtml = sermon.body.replace(/<\/p>/g, "\n\n").replace(/<(?:.|\n)*?>/gm, '');
        this.props.onCopySermon(bodyNoHtml);
        this.setState({ onCopyInProgress: false });
        EventTracking.Builder.onCopy(SearchEntity.Sermon, currentLocation);
    }

    onDrawerToggle = (open: boolean) => {
        this.setState({ open }, () => this.props.onDrawerToggle(open));
    }

    render() {
        const classes = this.props.classes;

        return (
            <>
                <React.Fragment>
                    <ListSubheader className={classes.title}>
                        Search Sermons
                    <IconButton onClick={() => this.onDrawerToggle(false)} className={classes.closeIcon}>
                            <CloseIcon />
                        </IconButton>
                    </ListSubheader>
                    <Divider />
                    <div className={classes.searchWrapper}>
                        <TextField
                            placeholder="Search Sermons"
                            variant="outlined"
                            className={classes.searchBar}
                            InputProps={{
                                className: classes.inputSearchText,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                                style: { height: 48 }
                            }}
                            value={this.state.queryText}
                            onChange={(e) => { this.setState({ queryText: e.target.value }, this.startSearch); }}
                        />
                    </div>
                    <React.Fragment>
                        {
                            !this.state.queryText
                            &&
                            <div className={classes.emptyState} />
                        }
                        {!this.state.sermonSearchResult && this.state.queryText.length > 0 &&
                            <div>
                                <CircularProgress className={classes.loading} size={35} /><br />
                                <Typography variant="h5" align='center'>
                                    Loading Sermons
                                </Typography>
                            </div>
                        }

                        {this.state.sermonSearchResult && this.state.sermonSearchResult.length > 0 && this.state.queryText.length > 0 &&
                            <List className={classNames('customScrollbar', classes.searchResults)}>
                                <InfiniteScroll
                                    pageStart={0}
                                    loadMore={() => this.searchSermons()}
                                    hasMore={this.state.totalSermonSearchResultCount > this.state.sermonSearchResult.length}
                                    useWindow={false}
                                    loader={<div key='loader'>
                                        <CircularProgress className={classes.loading} size={35} /><br />
                                        <Typography variant="h5" align='center'>
                                            Loading Sermons
                                    </Typography>
                                    </div>}
                                >
                                    {this.state.sermonSearchResult.map(sermon => {
                                        return (
                                            <React.Fragment key={sermon.id}>
                                                <MenuItem
                                                    key={sermon.id}
                                                    onClick={() => {
                                                        this.props.onSermonSelected(sermon.id);
                                                        EventTracking.Builder.contentDrawerOpen(SearchEntity.Sermon, currentLocation);
                                                    }}
                                                    className={classes.result}
                                                >
                                                    <ListItemText
                                                        primary={(
                                                            <span dangerouslySetInnerHTML={{ __html: StringHelper.highlightText(StringHelper.toTitleCase(sermon.title), this.state.queryText) }} />
                                                        )}
                                                        secondary={(
                                                            <>
                                                                <span>{sermon.contributor} | {RatingHelper.convertToStars(sermon.rating, sermon.id)}</span>
                                                                <Typography component="span" variant="body2" dangerouslySetInnerHTML={{ __html: StringHelper.highlightText(sermon.description, this.state.queryText) }} />
                                                            </>
                                                        )}
                                                    />
                                                    {/* <ListItemSecondaryAction className={classes.copyIcon}>
                                                        {
                                                            this.state.onCopyInProgress
                                                                ?
                                                                <CircularProgress className={classes.copyInProgress} size={20} />
                                                                :
                                                                <Tooltip title='Copy Sermon'>
                                                                    <IconButton onClick={() => this.onCopySermon(sermon)}>
                                                                        <FileCopyIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                        }
                                                    </ListItemSecondaryAction> */}
                                                </MenuItem>
                                                <Divider />
                                            </React.Fragment>
                                        );
                                    })}
                                </InfiniteScroll>
                            </List>
                        }
                        {this.state.sermonSearchResult && this.state.sermonSearchResult.length === 0 &&
                            <div className={classes.placeHolderText}>
                                <ErrorIcon className={classes.placeHolderIcon} /><br />
                                No Sermons Found
                                    </div>
                        }
                    </React.Fragment>
                </React.Fragment>
            </>
        );
    }
}

export function SermonSearch(props: Props){
    const classes = useStyles();

    return <SermonSearchClassComponent {...props} classes={classes} />
}
